/**
 * Auth + tenant context (Block 6.A / ADR-024 D7 + D7-bis).
 *
 * Two responsibilities:
 *   1. Boot SWA `/.auth/me` to determine sign-in state. Unauthenticated →
 *      surface a Sign-in CTA (App.tsx); authenticated → fetch whoami.
 *   2. Hardcode the bootstrap tenant slug as `'peace'` per D7-bis. ONE
 *      site (this constant); routes layer references it for the root
 *      redirect; whoami reconciliation runs in parallel and replaces the
 *      URL via navigate(replace: true) if the resolved slug differs (Era
 *      B stale-bookmark recovery — never fires in Phase 1 single-tenant).
 *
 * Era B replacement path (forward-declared per docs/planning/era-b-prerequisites.md):
 *   - Replace defaultTenant constant with whoami-derived tenant resolution.
 *   - Add tenant-picker to AppShell when whoami.active_engagements.length > 1.
 *
 * One-site discipline: defaultTenant is the SINGLE source of truth for
 * the bootstrap slug literal `'peace'`. Permitted consumers:
 *   - src/routes.tsx (root-redirect target).
 *   - src/App.tsx (initial whoami probe + TenantContext bootstrap).
 * The discipline guards against a NEW slug literal accumulating
 * elsewhere (someone hardcoding `'peace'` in a fetch URL or filter).
 * CI grep at PR time confirms no bare 'peace' string outside this file
 * + the two consumers above.
 */

import { createContext, useContext } from 'react';
import type { ClientPrincipal, WhoamiResponse } from './types';
import { apiErrorFrom } from './api';
// Phase 1.11 — frontend mirror lives at src/lib/capabilities.ts.
// canOwn re-exports below as a backward-compat alias.
import { canOwn as canOwnImpl } from './capabilities';

/**
 * Bootstrap tenant slug for Phase 1. Single operating tenant ('peace').
 * Used ONLY as the root-route redirect target before whoami fires.
 * Whoami reconciliation in TenantContext below will replace the URL if
 * the user's resolved tenant differs (Era B; never Phase 1).
 *
 * DO NOT consult this constant for any tenant-aware operation; pull
 * the slug from TenantContext (whoami-derived).
 */
export const defaultTenant = 'peace';

export interface TenantState {
  /** SWA principal — null if unauthenticated, undefined if still loading. */
  principal: ClientPrincipal | null | undefined;
  /** Whoami response — null if not yet fetched or unauthenticated. */
  whoami: WhoamiResponse | null;
  /** Currently-active tenant slug. Phase 1: always 'peace' once whoami resolves. */
  tenantSlug: string;
  /** Error from whoami (e.g. 403 membership_denied for cross-tenant URL). */
  error: string | null;
  /**
   * ADR-0046 — re-fetch whoami in place (App.tsx wires it). The
   * Permissions tab calls this after a toggle/revert so the acting
   * owner's own affordances pick up the fresh effective capability set
   * immediately; other signed-in users update on their next app load or
   * refresh (the server enforces the change on their very next request
   * regardless). Optional so bare TenantState literals (tests) need no
   * churn — callers use `refreshWhoami?.()`.
   */
  refreshWhoami?: () => Promise<void>;
}

export const TenantContext = createContext<TenantState>({
  principal: undefined,
  whoami: null,
  tenantSlug: defaultTenant,
  error: null,
});

export function useTenant(): TenantState {
  return useContext(TenantContext);
}

/**
 * Phase 1: tenantSlug is always 'peace'. Era B: derive from whoami's
 * tenant_id by reverse-mapping through getTenantConfig OR storing the
 * slug alongside tenant_id when seeded.
 *
 * For Phase 1 we accept the simplification: the URL-prefix slug is the
 * source of truth in the app's URL state; whoami confirms tenant_id
 * matches; mismatches surface as an error in TenantState. This avoids
 * the resolution roundtrip until Era B actually requires it.
 *
 * Who the platform says is signed in. `null` only for a 200 whose
 * `clientPrincipal` is null; any other answer (a 503, a 429) or a failed
 * request throws, so a failed check never reads as "nobody signed in".
 * Asked with no-store: the answer is never taken from the HTTP cache.
 */
export async function fetchPrincipal(): Promise<ClientPrincipal | null> {
  const r = await fetch('/.auth/me', { cache: 'no-store' });
  if (!r.ok) throw await apiErrorFrom(r, 'GET /.auth/me');
  const data = (await r.json()) as { clientPrincipal: ClientPrincipal | null };
  return data.clientPrincipal;
}

/**
 * @deprecated Phase 1.11 — use `canManageTeam(whoami)` from
 * src/lib/capabilities.ts. Aliased here for backward-compat with
 * AppShell + AdminReviewTabs consumers; future code should import
 * `canManageTeam` directly.
 */
export const canOwn = canOwnImpl;

export async function fetchWhoami(slug: string): Promise<WhoamiResponse> {
  const r = await fetch(`/api/t/${encodeURIComponent(slug)}/v1/whoami`);
  if (!r.ok) throw await apiErrorFrom(r, 'GET /whoami');
  return (await r.json()) as WhoamiResponse;
}
